import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, message, Spin, Tooltip } from 'antd';
import {
  DownOutlined,
  UpOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignTopOutlined,
} from '@ant-design/icons';
import { usePosBrowseCombos, usePosBrowseVariants } from '../hooks/usePosBrowse';
import { usePosCartAdder } from '../hooks/usePosCartAdder';
import { useActiveFlashSales } from '@/hooks/usePromotions';
import { SuggestProductCard, comboToCard, variantToCard, type SuggestCardData } from './SuggestProductCard';
import type { ActiveFlashSaleItem } from '@/services/promotion.service';
import type {
  FulfillmentMode,
  PosComboSearchItem,
  PosVariant,
  PosVariantDetailItem,
} from '../types';

const HIDDEN_KEY = 'pos_suggest_panel_hidden';
const EXPANDED_KEY = 'pos_suggest_panel_expanded';
const COLLAPSED_HEIGHT = 140;
const EXPANDED_HEIGHT = 380;

interface Props {
  fulfillmentMode: FulfillmentMode;
  storeId: string | undefined;
  onVariantFound: (variant: PosVariant) => void;
  onComboFound: (combo: PosComboSearchItem, details: PosVariantDetailItem[]) => void;
  onFlashSaleFound: (variant: PosVariant, flashSaleItemId: string, flashPrice: number) => void;
  /**
   * TCR-1143 ad-hoc fix: tab "Flash Sale" mặc định BẬT (giữ nguyên hành vi
   * cũ trên POS — call site ở đó không truyền prop này). Trang Orders/edit
   * dùng chung component này nhưng đi qua `orderService.update`
   * (orders-aggregate.service.ts), một endpoint BE hoàn toàn CHƯA có wiring
   * flash sale (không tạo FlashSaleReservation, không trừ
   * remaining_quantity/sold_quantity, không kiểm tra max_per_customer) —
   * khác Combo đã được endpoint đó hỗ trợ qua `combo_group_id`. Nếu để tab
   * mở ở Orders/edit, thu ngân bấm thẻ flash sale rồi lưu đơn sẽ ÂM THẦM
   * lách qua toàn bộ enforcement tồn kho/giới hạn — rủi ro bán vượt tồn
   * (oversell) thật, không phải lỗi cosmetic. Orders/edit truyền
   * `enableFlashSale={false}` để ẩn hẳn tab cho tới khi BE hỗ trợ flash sale
   * trên endpoint update (ticket follow-up riêng).
   */
  enableFlashSale?: boolean;
}

/**
 * Lưới gợi ý sản phẩm/combo ở đáy cột đơn hàng POS — TCR-1126.
 *
 * Thu ngân chọn trạng thái ẩn/mở rộng một lần rồi giữ nguyên cả ca, nên trạng
 * thái lưu localStorage thay vì reset mỗi lần vào màn.
 */
export function ProductSuggestPanel({
  fulfillmentMode,
  storeId,
  onVariantFound,
  onComboFound,
  onFlashSaleFound,
  enableFlashSale = true,
}: Props) {
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDDEN_KEY) === '1');
  const [expanded, setExpanded] = useState(() => localStorage.getItem(EXPANDED_KEY) === '1');
  const [tab, setTab] = useState<'all' | 'combo' | 'flash'>('all');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Phòng thủ: nếu `enableFlashSale` đổi từ true → false trong lúc tab đang
  // ở 'flash' (vd trang cha đổi context bất ngờ), tự đưa tab về 'all' — chứ
  // không để người dùng kẹt ở một tab không còn nút bấm nào để thoát ra.
  useEffect(() => {
    if (!enableFlashSale) setTab((prev) => (prev === 'flash' ? 'all' : prev));
  }, [enableFlashSale]);

  const variants = usePosBrowseVariants(fulfillmentMode, !hidden && tab === 'all');
  const combos = usePosBrowseCombos(fulfillmentMode, !hidden && tab === 'combo');
  const flashSalesQuery = useActiveFlashSales(storeId, enableFlashSale && !hidden && tab === 'flash');
  const flashSaleItems = useMemo<ActiveFlashSaleItem[]>(
    () => (flashSalesQuery.data ?? []).flatMap((fs) => fs.items),
    [flashSalesQuery.data],
  );
  // Nhãn hạn kết thúc — lấy từ flash sale ĐẦU TIÊN chứa item đó (item không tự
  // mang end_time, chỉ flash sale cha có). Nếu 1 item vô tình nằm trong nhiều
  // flash sale cùng lúc (không nên xảy ra theo nghiệp vụ, nhưng không throw ở
  // đây), lấy hạn của campaign xuất hiện trước trong response.
  const flashSaleEndTimeByItemId = useMemo(() => {
    const map = new Map<string, string>();
    for (const fs of flashSalesQuery.data ?? []) {
      for (const item of fs.items) {
        if (!map.has(item.id)) map.set(item.id, fs.end_time);
      }
    }
    return map;
  }, [flashSalesQuery.data]);
  const active =
    tab === 'all'
      ? variants
      : tab === 'combo'
        ? combos
        : {
            items: flashSaleItems,
            hasMore: false,
            loadMore: () => {},
            isLoading: flashSalesQuery.isLoading,
            isError: flashSalesQuery.isError,
          };

  const { addByQrCode, selectCombo, addFlashSaleItem } = usePosCartAdder({
    fulfillmentMode,
    onVariantFound,
    onComboFound,
    onFlashSaleFound,
  });

  const cards = useMemo(() => {
    if (tab === 'all') return variants.items.map((item) => ({ card: variantToCard(item), raw: item }));
    if (tab === 'combo') return combos.items.map((item) => ({ card: comboToCard(item), raw: item }));
    return flashSaleItems
      // Spec §6.1: món HẾT SUẤT vẫn hiển thị (thẻ tự khoá qua `stock <= 0`),
      // ĐẢO quyết định lọc bỏ của TCR-1143 — ẩn đi thì thu ngân không phân
      // biệt nổi "chương trình không có món này" với "món này vừa hết suất".
      //
      // `flash_price == null` thì VẪN lọc: không có giá flash nghĩa là chương
      // trình chưa khai giá, thẻ sẽ hiện giá 0đ và thêm vào giỏ thành hàng
      // tặng không.
      .filter((item) => item.flash_price != null)
      .map((item) => {
        const endTime = flashSaleEndTimeByItemId.get(item.id);
        const card: SuggestCardData = {
          key: item.id,
          name: item.product_name ?? item.product_id,
          code: item.variant_code ?? '',
          image: '',
          price: item.flash_price as number,
          originalPrice: item.original_price,
          stock: item.remaining_quantity,
          isCombo: false,
          badgeLabel: 'Flash',
          subLabel: endTime
            ? `Kết thúc lúc ${new Date(endTime).toLocaleString('vi-VN', {
                hour: '2-digit',
                minute: '2-digit',
                day: '2-digit',
                month: '2-digit',
              })}`
            : undefined,
        };
        return { card, raw: item };
      });
  }, [tab, variants.items, combos.items, flashSaleItems, flashSaleEndTimeByItemId]);

  // Tự lấp đầy trang khi vùng cuộn KHÔNG tràn (`scrollHeight <= clientHeight`)
  // mà vẫn còn hàng để tải — QA phát hiện bấm "Mở rộng" đổi chiều cao khung
  // xuống 380px, đúng 24 thẻ trang 1 vừa khít nên KHÔNG sinh thanh cuộn, sự
  // kiện `scroll` không bao giờ bắn, `loadMore` không bao giờ được gọi và
  // thu ngân vĩnh viễn không thấy sản phẩm thứ 25 trở đi. Effect này chạy lại
  // mỗi khi số thẻ / `hasMore` / trạng thái mở rộng / tab đổi — tự gọi
  // `loadMore` tới khi tràn khung hoặc hết hàng (`hasMore` false thì dừng,
  // không có vòng lặp vô tận).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (active.hasMore && el.scrollHeight <= el.clientHeight) {
      active.loadMore();
    }
  }, [cards.length, active.hasMore, active.loadMore, expanded, tab]);

  const toggleHidden = useCallback((next: boolean) => {
    setHidden(next);
    localStorage.setItem(HIDDEN_KEY, next ? '1' : '0');
  }, []);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      localStorage.setItem(EXPANDED_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      // 60px trước đáy — tải sớm để lưới không khựng giữa cú cuộn.
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) active.loadMore();
    },
    [active],
  );

  if (hidden) {
    return (
      <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', padding: '2px 12px' }}>
        <Tooltip title="Hiện gợi ý sản phẩm">
          <Button
            data-testid="pos-suggest-show-btn"
            size="small"
            type="text"
            icon={<UpOutlined />}
            onClick={() => toggleHidden(false)}
          />
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      data-testid="pos-suggest-panel"
      style={{
        // `relative` để viên thuốc Mở rộng/Thu hẹp neo theo MÉP TRÊN của panel
        // (đường kẻ), không phải theo hàng tab bên dưới.
        position: 'relative',
        flexShrink: 0,
        borderTop: '1px solid #e0e0e0',
        background: '#fafafa',
      }}
    >
      {/* Viên thuốc ĐÈ LÊN đường kẻ mép trên, chính giữa panel — theo mẫu tham
          chiếu. `top: 0` + `translate(-50%, -50%)` đặt TÂM nút đúng trên đường
          kẻ; canh giữa bằng `margin: auto` trong hàng tab sẽ lệch, vì cụm tab
          bên trái và nút ẩn bên phải rộng khác nhau. */}
      <Button
        data-testid="pos-suggest-expand-btn"
        size="small"
        type="text"
        // Mũi tên chỉ ĐÚNG hướng lưới sắp đi: chưa mở rộng thì mũi tên hướng
        // lên (lưới sẽ cao lên), đang mở rộng thì hướng xuống (lưới sẽ thấp
        // xuống). Ngược lại là nói dối thao tác.
        icon={expanded ? <VerticalAlignBottomOutlined /> : <VerticalAlignTopOutlined />}
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          height: 24,
          lineHeight: '22px',
          borderRadius: 999,
          border: '1px solid #e0e0e0',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          padding: '0 14px',
          fontSize: 12,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
        onClick={toggleExpanded}
      >
        {expanded ? 'Thu hẹp' : 'Mở rộng'}
      </Button>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 12px',
        }}
      >
        <Button
          data-testid="pos-suggest-tab-all"
          size="small"
          type={tab === 'all' ? 'primary' : 'text'}
          style={tab === 'all' ? { background: '#a4193d', borderColor: '#a4193d' } : undefined}
          onClick={() => setTab('all')}
        >
          Tất cả
        </Button>
        <Button
          data-testid="pos-suggest-tab-combo"
          size="small"
          type={tab === 'combo' ? 'primary' : 'text'}
          style={tab === 'combo' ? { background: '#a4193d', borderColor: '#a4193d' } : undefined}
          onClick={() => setTab('combo')}
        >
          Combo
        </Button>
        {enableFlashSale && (
          <Button
            data-testid="pos-suggest-tab-flash"
            size="small"
            type={tab === 'flash' ? 'primary' : 'text'}
            style={tab === 'flash' ? { background: '#a4193d', borderColor: '#a4193d' } : undefined}
            onClick={() => setTab('flash')}
          >
            Flash Sale
          </Button>
        )}
        <Tooltip title="Ẩn gợi ý sản phẩm">
          <Button
            data-testid="pos-suggest-hide-btn"
            size="small"
            type="text"
            icon={<DownOutlined />}
            style={{ marginLeft: 'auto' }}
            onClick={() => toggleHidden(true)}
          />
        </Tooltip>
      </div>

      <div
        data-testid="pos-suggest-scroll"
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          height: expanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT,
          overflowY: 'auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          padding: '0 12px 8px',
          alignContent: 'flex-start',
        }}
      >
        {/* TCR-1143 (finding #4, review cuối): trước đây `active.isError` bail
            ra `return null` cho CẢ panel (dòng cũ nằm ngay trước hàng tab bên
            trên) — nghĩa là nếu RIÊNG endpoint flash sale lỗi, thu ngân mất
            luôn cả 2 tab "Tất cả"/"Combo" và nút ẩn/hiện cho tới khi reload
            trang. Giờ hàng tab + khung panel LUÔN render; lỗi chỉ thay nội
            dung của vùng cuộn hiện tại bằng 1 trạng thái rỗng có thông báo —
            2 tab còn lại vẫn bấm được và tải dữ liệu bình thường vì `active`
            chỉ phản ánh tab đang chọn (mỗi tab có query/enabled riêng, không
            tab nào phụ thuộc tab khác). */}
        {active.isError && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              tab === 'flash'
                ? 'Không tải được flash sale, thử lại sau'
                : tab === 'combo'
                  ? 'Không tải được danh sách combo, thử lại sau'
                  : 'Không tải được danh sách sản phẩm, thử lại sau'
            }
            style={{ margin: 'auto' }}
          />
        )}
        {!active.isError && active.isLoading && <Spin size="small" style={{ margin: 'auto' }} />}
        {!active.isError && !active.isLoading && cards.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Chưa có sản phẩm bán được ở POS"
            style={{ margin: 'auto' }}
          />
        )}
        {!active.isError && cards.map(({ card, raw }) => (
          <SuggestProductCard
            key={card.key}
            data={card}
            onSelect={() => {
              if (card.isCombo) {
                void selectCombo(raw as PosComboSearchItem);
                return;
              }
              if (tab === 'flash') {
                const flashItem = raw as ActiveFlashSaleItem;
                if (!flashItem.variant_code) {
                  void message.warning('Sản phẩm flash sale thiếu mã biến thể, không thêm được');
                  return;
                }
                void addFlashSaleItem(
                  flashItem.variant_code,
                  flashItem.id,
                  flashItem.flash_price as number,
                );
                return;
              }
              void addByQrCode(card.code);
            }}
          />
        ))}
      </div>
    </div>
  );
}
